import { RestApiSenderAuthType, RestApiSenderConfig } from 'ihub/common';
import { AdvancedTourStep } from '../../shared-types';
import { RestApiSenderTourProps } from './rest-api-sender-tour-props';

export function getRestApiSenderAuthorizationSteps({
	defaultStyles,
	microTaskConfigType,
}: Readonly<RestApiSenderTourProps>): AdvancedTourStep[] {
	if (!microTaskConfigType) return [];
	const config = microTaskConfigType as RestApiSenderConfig;

	const getAuthSteps = (): AdvancedTourStep[] => {
		switch (config.authType) {
			case RestApiSenderAuthType.NoAuth:
				return [];
			case RestApiSenderAuthType.BasicAuth:
				return [
					{
						id: 'basic-auth-username',
						selector: '[data-tour="basic-auth-username"]',
						content: 'Enter the username used for Basic Authentication. This value is required.',
					},
					{
						id: 'use-variable-token',
						selector: '[data-tour="use-variable-token"]',
						content:
							'Toggle this option to manually enter a password. If unchecked, you can select a password from available flow parameters.',
					},
					{
						id: 'basic-auth-password',
						selector: '[data-tour="basic-auth-password"]',
						content:
							'Enter the password for Basic Authentication, or select one from the available flow parameters depending on your configuration.',
					},
				];
			case RestApiSenderAuthType.APIKey:
				return [
					{
						id: 'api-key-name',
						selector: '[data-tour="api-key-name"]',
						content:
							'Enter the key name for the API key (default is "x-api-key"). This will be used as the key in the key-value pair.',
					},
					{
						id: 'api-key-value',
						selector: '[data-tour="api-key-value"]',
						content:
							'Enter the value for the API key. You can also select a flow parameter if "Variable Value" is not enabled.',
					},
					{
						id: 'api-key-add-to',
						selector: '[data-tour="api-key-add-to"]',
						content:
							'Select where the API key should be added in the request—either as a header or as a query parameter.',
					},
				];
			case RestApiSenderAuthType.AWSSignature:
				return [
					{
						id: 'aws-access-key',
						selector: '[data-tour="aws-access-key"]',
						content:
							'Enter or select your AWS Access Key. This is used to authenticate requests to AWS services.',
					},
					{
						id: 'aws-secret-key',
						selector: '[data-tour="aws-secret-key"]',
						content:
							'Enter or select your AWS Secret Key associated with the Access Key. Keep this value secure.',
					},
					{
						id: 'aws-region',
						selector: '[data-tour="aws-region"]',
						content:
							'Specify the AWS Region where the target service is hosted. This determines where the request will be sent.',
					},
					{
						id: 'aws-service-name',
						selector: '[data-tour="aws-service-name"]',
						content: 'Enter the AWS Service Name you want to interact with (e.g., S3, DynamoDB).',
					},
					{
						id: 'aws-session-token',
						selector: '[data-tour="aws-session-token"]',
						content:
							'Optional: Provide a Session Token if using temporary credentials (such as IAM roles or STS). Leave blank if not required.',
					},
				];
			case RestApiSenderAuthType.JWTBearer:
				return [
					{
						id: 'jwt-algorithm',
						selector: '[data-tour="jwt-algorithm"]',
						content:
							'Select the algorithm used to sign the JWT bearer token (e.g., HS256). This determines how the token is securely generated.',
					},
					{
						id: 'jwt-secret',
						selector: '[data-tour="jwt-secret"]',
						content:
							'Enter or select the secret used to sign the JWT. This can be a flow parameter or a manually entered value. Keep this secure.',
					},
					{
						id: 'jwt-payload',
						selector: '[data-tour="jwt-payload"]',
						content:
							'Optional: Add payload claims to the JWT. Claims are statements about an entity (typically the user) along with any additional data you want to include.',
					},
					{
						id: 'jwt-request-header-prefix',
						selector: '[data-tour="jwt-request-header-prefix"]',
						content:
							'Optional: Define the prefix added before the JWT in the request header (default is "Bearer").',
					},
				];
			case RestApiSenderAuthType.BearerToken:
				return [
					{
						id: 'auth-token',
						selector: '[data-tour="auth-token"]',
						content:
							'Enter the token value used for authentication. You can also select a flow parameter if "Variable Token" is not enabled.',
					},
				];
			default:
				return [];
		}
	};

	return [
		{
			id: 'auth-type-select',
			selector: '[data-tour="auth-type-select"]',
			content: 'Used to select the type of authorization to use. ',
		},
		...(config.authConfig ? getAuthSteps() : []),
	].map(tourItem => ({ ...tourItem, styles: defaultStyles }));
}
